import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import T from '../styles/theme';
import { fmtDollar, Checkbox } from '../components/UI';
import Icon from '../components/Icon';
import { useStore } from '../data/store';

// ─── DEFAULT VIEWS ──────────────────────────────
const DEFAULT_VIEWS = [
  { id: "all", name: "All Companies", filters: [], system: true },
  { id: "has_deals", name: "Has Open Deals", filters: [{ field: "has_open_deals", value: "true" }] },
  { id: "won", name: "Has Won Revenue", filters: [{ field: "has_won", value: "true" }] },
  { id: "high_health", name: "Healthy (80+)", filters: [{ field: "health_min", value: "80" }] },
  { id: "at_risk", name: "At Risk (<60)", filters: [{ field: "health_max", value: "59" }] },
];

function applyCompanyFilter(company, f) {
  const { field, value } = f;
  if (!value) return true;
  switch (field) {
    case "industry": return (company.industry || "").toLowerCase() === value.toLowerCase();
    case "has_open_deals": return value === "true" ? company.openDeals.length > 0 : company.openDeals.length === 0;
    case "has_won": return value === "true" ? company.wonVal > 0 : company.wonVal === 0;
    case "health_min": return (company.score || 0) >= Number(value);
    case "health_max": return (company.score || 0) <= Number(value);
    default: return true;
  }
}

// Enrich real company rows with derived stats (contacts, deals, health).
// `id`, `name`, `industry`, `city`, `state` now come from the real
// companies table row — NOT from the first contact's data.
function enrichCompany(company, CONTACTS, DEALS) {
  const employees = CONTACTS.filter(c => c.company_id === company.id);
  const deals = DEALS.filter(d => d.company_id === company.id);
  const totalDealVal = deals.reduce((a, d) => a + (d.val || 0), 0);
  const wonDeals = deals.filter(d => d.stage === "Closed Won");
  const wonVal = wonDeals.reduce((a, d) => a + (d.val || 0), 0);
  const openDeals = deals.filter(d => d.stage !== "Closed Won" && d.stage !== "Closed Lost");
  const openDealVal = openDeals.reduce((a, d) => a + (d.val || 0), 0);
  return {
    ...company,
    industry: company.industry || "Other",
    contacts: employees,
    deals, totalDealVal, wonVal, openDeals, wonDeals, openDealVal,
    score: employees.length > 0
      ? Math.round(employees.reduce((a, c) => a + (c.score || 0), 0) / employees.length)
      : 0,
  };
}

export default function CompaniesPage({ onSelectContact, onSelectDeal, onSelectCompany, isMobile }) {
  const store = useStore();
  const { contacts: CONTACTS, deals: DEALS, companies: RAW_COMPANIES } = store;
  const COMPANIES = useMemo(
    () => (RAW_COMPANIES || []).map(co => enrichCompany(co, CONTACTS, DEALS)),
    [RAW_COMPANIES, CONTACTS, DEALS]
  );

  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [selected, setSelected] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // ── View picker ──
  const [activeViewId, setActiveViewId] = useState("all");
  const [filters, setFilters] = useState([]);
  const [showViewPicker, setShowViewPicker] = useState(false);
  const [viewSearch, setViewSearch] = useState("");
  const viewPickerRef = useRef(null);
  const [viewKey, setViewKey] = useState(0);

  // ── Column editor ──
  const ALL_COLUMNS = useMemo(() => [
    { id: "name", label: "Company", required: true },
    { id: "industry", label: "Industry" },
    { id: "contacts", label: "Contacts", w: 90 },
    { id: "openDeals", label: "Open Deals", w: 100 },
    { id: "pipeline", label: "Pipeline", w: 110 },
    { id: "won", label: "Won", w: 110 },
    { id: "health", label: "Health", w: 80 },
    { id: "location", label: "Location", w: 130 },
  ], []);
  const DEFAULT_COLS = ["name", "industry", "contacts", "openDeals", "pipeline", "won", "health"];
  const [visibleCols, setVisibleCols] = useState(DEFAULT_COLS);
  const [showColEditor, setShowColEditor] = useState(false);
  const [colEditorDraft, setColEditorDraft] = useState(DEFAULT_COLS);
  const [colSearch, setColSearch] = useState("");
  const dragItem = useRef(null);
  const dragOverRef = useRef(null);

  const views = DEFAULT_VIEWS;
  const activeView = views.find(v => v.id === activeViewId) || views[0];

  useEffect(() => {
    const h = (e) => { if (viewPickerRef.current && !viewPickerRef.current.contains(e.target)) setShowViewPicker(false); };
    if (showViewPicker) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [showViewPicker]);

  const switchView = useCallback((viewId) => {
    setActiveViewId(viewId);
    const view = views.find(v => v.id === viewId) || views[0];
    setFilters(view.filters ? view.filters.map(f => ({ ...f })) : []);
    setViewKey(k => k + 1);
    setCurrentPage(1);
  }, []);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };
  const sortIcon = (col) => sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const filtered = useMemo(() => {
    let result = [...COMPANIES];
    if (filters.length > 0) result = result.filter(co => filters.every(f => applyCompanyFilter(co, f)));
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(co => co.name.toLowerCase().includes(q) || co.industry.toLowerCase().includes(q) || (co.city + " " + co.state).toLowerCase().includes(q));
    }
    result.sort((a, b) => {
      let av, bv;
      switch (sortCol) {
        case "name": av = a.name; bv = b.name; break;
        case "industry": av = a.industry; bv = b.industry; break;
        case "contacts": av = a.contacts.length; bv = b.contacts.length; break;
        case "openDeals": av = a.openDeals.length; bv = b.openDeals.length; break;
        case "pipeline": av = a.openDealVal; bv = b.openDealVal; break;
        case "won": av = a.wonVal; bv = b.wonVal; break;
        case "health": av = a.score; bv = b.score; break;
        case "location": av = a.city + a.state; bv = b.city + b.state; break;
        default: av = a.name; bv = b.name;
      }
      if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
      return sortDir === "asc" ? String(av || "").localeCompare(String(bv || "")) : String(bv || "").localeCompare(String(av || ""));
    });
    return result;
  }, [COMPANIES, search, sortCol, sortDir, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const allSelected = filtered.length > 0 && filtered.every(co => selected.has(co.id));
  const toggleAll = () => { if (allSelected) setSelected(new Set()); else setSelected(new Set(filtered.map(co => co.id))); };
  const toggleOne = (id) => { setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };

  const applyColumns = (cols) => {
    setVisibleCols(cols);
    try { localStorage.setItem("cf_company_columns", JSON.stringify(cols)); } catch {}
  };

  const hdrBtn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" };

  // ── LIST VIEW ──
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>

      {/* ═══ VIEW DROPDOWN — matches Deals/People page ═══ */}
      <div style={{ padding: "14px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div ref={viewPickerRef} style={{ position: "relative" }}>
            <button onClick={() => { setShowViewPicker(!showViewPicker); setViewSearch(""); }}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: showViewPicker ? "rgba(255,255,255,0.08)" : "transparent", cursor: "pointer", fontFamily: "inherit", transition: "all .15s ease" }}
              onMouseEnter={e => { if (!showViewPicker) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
              onMouseLeave={e => { if (!showViewPicker) e.currentTarget.style.background = "transparent"; }}>
              <Icon name="company" size={14} color="#FAFAFA" />
              <span style={{ fontSize: 15, fontWeight: 400, color: "#FAFAFA" }}>{activeView.name}</span>
              <span style={{ fontSize: 10, color: "#FAFAFA", opacity: 0.5, marginLeft: 2 }}>▾</span>
            </button>
            {showViewPicker && <div className="cf-dropdown-popup" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, width: 300, background: "#33363D", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 24px 64px rgba(0,0,0,0.80)", zIndex: 99999, overflow: "hidden" }}>
              <div style={{ padding: "12px 14px 10px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
                <input autoFocus value={viewSearch} onChange={e => setViewSearch(e.target.value)} placeholder="Search views..." style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.10)", background: "rgba(255,255,255,0.04)", color: "#FAFAFA", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ maxHeight: 320, overflowY: "auto", padding: "8px 10px" }}>
                {views.filter(v => !viewSearch || v.name.toLowerCase().includes(viewSearch.toLowerCase())).map(v => (
                  <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                    <div onClick={() => { switchView(v.id); setShowViewPicker(false); }} style={{ display: "flex", alignItems: "center", flex: 1, padding: "11px 14px", borderRadius: 8, cursor: "pointer", background: activeViewId === v.id ? "#4059AD" : "rgba(255,255,255,0.04)", color: activeViewId === v.id ? "#fff" : "#FAFAFA" }}
                      onMouseEnter={e => { if (activeViewId !== v.id) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                      onMouseLeave={e => { if (activeViewId !== v.id) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                      <span style={{ fontSize: 13, fontWeight: activeViewId === v.id ? 600 : 400 }}>{v.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>}
          </div>
          <span style={{ fontSize: 12, color: "#FAFAFA", opacity: 0.5 }}>{filtered.length} companies</span>
        </div>
      </div>

      {/* ═══ HEADER BAR — search + buttons ═══ */}
      <div style={{ display: "flex", gap: 6, padding: "10px 24px 14px", alignItems: "center", flexShrink: 0 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
          style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(0,0,0,0.15)", fontSize: 12, fontFamily: "inherit", outline: "none", color: "#FAFAFA", width: 180 }} />
        <div style={{ flex: 1 }} />
        <button onClick={() => { setColEditorDraft([...visibleCols]); setColSearch(""); setShowColEditor(true); }} style={hdrBtn}>
          <Icon name="settings" size={12} color="#FAFAFA" /> Columns
        </button>
      </div>

      {/* ═══ COLUMN EDITOR MODAL ═══ */}
      {showColEditor && ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setShowColEditor(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)" }} />
          <div style={{ position: "relative", width: 700, maxWidth: "94vw", height: 520, maxHeight: "80vh", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 40px 100px rgba(0,0,0,0.40)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid rgba(217,219,221,0.08)", flexShrink: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>Edit Columns</span>
              <button onClick={() => setShowColEditor(false)} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: T.tx3 }}>×</button>
            </div>
            <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
              <div style={{ width: "50%", borderRight: "1px solid rgba(217,219,221,0.08)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ padding: "12px 16px 8px", flexShrink: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", marginBottom: 8 }}>AVAILABLE FIELDS</div>
                  <input value={colSearch} onChange={e => setColSearch(e.target.value)} placeholder="Search fields..."
                    style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 11, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: "4px 16px 12px" }}>
                  {ALL_COLUMNS.filter(c => !c.required).filter(c => !colSearch || c.label.toLowerCase().includes(colSearch.toLowerCase())).map(col => {
                    const isActive = colEditorDraft.includes(col.id);
                    return (
                      <div key={col.id} onClick={() => { if (isActive) setColEditorDraft(p => p.filter(c => c !== col.id)); else setColEditorDraft(p => [...p, col.id]); }}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", marginBottom: 2, borderRadius: 8, cursor: "pointer", transition: "background 0.1s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ width: 20, height: 20, borderRadius: 8, background: isActive ? "#4059AD" : "transparent", border: isActive ? "1.5px solid #4059AD" : "1.5px solid rgba(217,219,221,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {isActive && <span style={{ color: "#fff", fontSize: 11, fontWeight: 400 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 12, color: T.txt }}>{col.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ width: "50%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ padding: "12px 16px 8px", flexShrink: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>SELECTED COLUMNS ({colEditorDraft.length})</div>
                  <div style={{ fontSize: 9, color: T.tx3, marginTop: 2 }}>Drag to reorder</div>
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: "4px 16px 12px" }}>
                  {colEditorDraft.map((colId, idx) => {
                    const col = ALL_COLUMNS.find(c => c.id === colId);
                    if (!col) return null;
                    return (
                      <div key={colId} draggable
                        onDragStart={() => { dragItem.current = idx; }}
                        onDragEnter={() => { dragOverRef.current = idx; }}
                        onDragOver={e => e.preventDefault()}
                        onDragEnd={() => {
                          const from = dragItem.current, to = dragOverRef.current;
                          if (from === null || to === null || from === to) return;
                          const n = [...colEditorDraft]; const item = n.splice(from, 1)[0]; n.splice(to, 0, item);
                          setColEditorDraft(n); dragItem.current = null; dragOverRef.current = null;
                        }}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", marginBottom: 3, borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(217,219,221,0.08)", cursor: "grab", userSelect: "none" }}>
                        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", cursor: "grab", lineHeight: 1 }}>⠿</span>
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 400, color: T.txt }}>{col.label}</span>
                        {!col.required && <button onClick={e => { e.stopPropagation(); setColEditorDraft(p => p.filter(c => c !== colId)); }}
                          style={{ width: 22, height: 22, borderRadius: 6, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: T.tx3 }}>×</button>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", padding: "12px 20px", borderTop: "1px solid rgba(217,219,221,0.08)", flexShrink: 0 }}>
              <button onClick={() => setColEditorDraft([...DEFAULT_COLS])} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>Reset</button>
              <button onClick={() => setShowColEditor(false)} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={() => { applyColumns([...colEditorDraft]); setShowColEditor(false); setColSearch(""); }} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, color: "#D9DBDD", background: T.blue, border: "none", cursor: "pointer", fontFamily: "inherit" }}>Apply</button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* ═══ TABLE ═══ */}
      <div key={"cv-" + viewKey} className="cf-page-enter" style={{ overflowX: "auto", padding: "0 24px", flex: 1 }}>
        <table style={{ width: "100%", minWidth: 800, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid " + T.bdr, position: "sticky", top: 0, background: "#2C2E33", zIndex: 2 }}>
              <th style={{ padding: "10px 12px", width: 36 }}>
                <Checkbox checked={allSelected} onChange={toggleAll} />
              </th>
              {visibleCols.map(colId => {
                const col = ALL_COLUMNS.find(c => c.id === colId);
                if (!col) return null;
                const align = colId === "name" ? "left" : "center";
                return (
                  <th key={col.id} onClick={() => handleSort(col.id)}
                    style={{ padding: "10px 12px", textAlign: align, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", cursor: "pointer", width: col.w || "auto", whiteSpace: "nowrap", userSelect: "none" }}>
                    <span>{col.label}<span style={{ color: sortCol === col.id ? "#FAFAFA" : "rgba(255,255,255,0.15)", fontSize: 11, marginLeft: 4 }}>{sortIcon(col.id)}</span></span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paginated.map(co => {
              const cellStyle = { padding: "8px 12px", cursor: "pointer", textAlign: "center" };
              return (
                <tr key={co.id}
                  style={{ borderBottom: "1px solid " + T.bdr, transition: "background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = T.bg}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "8px 12px" }} onClick={e => e.stopPropagation()}>
                    <Checkbox checked={selected.has(co.id)} onChange={() => toggleOne(co.id)} />
                  </td>
                  {visibleCols.map(colId => {
                    const click = () => onSelectCompany && onSelectCompany(co.id);
                    if (colId === "name") return <td key={colId} style={{ ...cellStyle, textAlign: "left", fontSize: 12, fontWeight: 400, color: T.txt }} onClick={click}>{co.name}</td>;
                    if (colId === "industry") return <td key={colId} style={{ ...cellStyle, fontSize: 11, color: T.tx3 }} onClick={click}>{co.industry}</td>;
                    if (colId === "contacts") return <td key={colId} style={{ ...cellStyle, fontSize: 11, color: "#FAFAFA" }} onClick={click}>{co.contacts.length}</td>;
                    if (colId === "openDeals") return <td key={colId} style={{ ...cellStyle, fontSize: 11, color: "#FAFAFA" }} onClick={click}>{co.openDeals.length}</td>;
                    if (colId === "pipeline") return <td key={colId} style={{ ...cellStyle, fontSize: 11, color: "#FAFAFA" }} onClick={click}>{fmtDollar(co.openDealVal)}</td>;
                    if (colId === "won") return <td key={colId} style={{ ...cellStyle, fontSize: 11, color: "#FAFAFA" }} onClick={click}>{fmtDollar(co.wonVal)}</td>;
                    if (colId === "health") return <td key={colId} style={{ ...cellStyle, fontSize: 11, color: "#FAFAFA" }} onClick={click}>{co.score}</td>;
                    if (colId === "location") return <td key={colId} style={{ ...cellStyle, fontSize: 11, color: T.tx3 }} onClick={click}>{co.city}{co.state ? ", " + co.state : ""}</td>;
                    return <td key={colId} style={cellStyle} onClick={click}>—</td>;
                  })}
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={visibleCols.length + 1} style={{ padding: 40, textAlign: "center", color: T.tx3, fontSize: 12 }}>No companies found</td></tr>
            )}
          </tbody>
        </table>
        {filtered.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", flexShrink: 0, borderTop: "1px solid rgba(217,219,221,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 11, color: T.tx3 }}>{((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                {[20, 50, 100].map(size => (
                  <button key={size} onClick={() => { setPageSize(size); setCurrentPage(1); }} style={{ padding: "2px 6px", borderRadius: 4, fontSize: 10, border: "none", background: pageSize === size ? "rgba(64,89,173,0.15)" : "transparent", color: pageSize === size ? "#FAFAFA" : T.tx3, cursor: "pointer", fontFamily: "inherit" }}>{size}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: "4px 8px", borderRadius: 4, border: "none", background: "transparent", color: currentPage === 1 ? "rgba(250,250,250,0.15)" : "#FAFAFA", cursor: currentPage === 1 ? "default" : "pointer", fontFamily: "inherit", fontSize: 12 }}>‹</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => { let pn; if (totalPages <= 5) pn = i + 1; else if (currentPage <= 3) pn = i + 1; else if (currentPage >= totalPages - 2) pn = totalPages - 4 + i; else pn = currentPage - 2 + i; return (
                <button key={pn} onClick={() => setCurrentPage(pn)} style={{ width: 28, height: 28, borderRadius: 6, fontSize: 10, border: "none", fontFamily: "inherit", background: currentPage === pn ? "rgba(64,89,173,0.15)" : "transparent", color: currentPage === pn ? "#FAFAFA" : T.tx3, cursor: "pointer" }}>{pn}</button>
              ); })}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ padding: "4px 8px", borderRadius: 4, border: "none", background: "transparent", color: currentPage === totalPages ? "rgba(250,250,250,0.15)" : "#FAFAFA", cursor: currentPage === totalPages ? "default" : "pointer", fontFamily: "inherit", fontSize: 12 }}>›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
